import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Point, StaticCanvas } from 'fabric';
import type {
  Canvas as FabricCanvas,
  FabricObject,
  TPointerEvent,
} from 'fabric';

import { useToolContext } from '@/context/ToolContext';
import { selectionStyles } from '@/config/options';

import toggleLineControlVisibility from '@/utils/toggleLineControlVisibility';
import calcArrowAngle from '@/utils/lines/calcArrowAngle';

import makePathLine from '@/utils/lines/makePathLine';
import makeCurveEnd from '@/utils/lines/makeCurveEnd';
import makeCurvePoint from '@/utils/lines/makeCurvePoint';
import makeArrowHead from '@/utils/lines/makeArrowHead';
import initialiseCanvas from '@/utils/initialiseCanvas';

interface CanvasProps {
  iconType?: string;
}

// Permissive shapes for the Fabric event payloads this component reads (target/selected/e/…).
interface FabricEvent {
  e?: TPointerEvent;
  target?: FabricObject;
  selected?: FabricObject[];
  deselected?: FabricObject[];
  multiSelect?: boolean;
}

const Canvas = (_props: CanvasProps) => {
  const ToolContextState = useToolContext();

  const t = useTranslations();

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // To be able to get the current state value within event listeners, states need to be saved in refs.
  // https://medium.com/geographit/accessing-react-state-in-event-listeners-with-usestate-and-useref-hooks-8cceee73c559
  const [lineCount, _setLineCount] = useState<string | number | null>(null);
  const lineCountStateRef = useRef(lineCount);
  const setLineCount = (
    lineCount: string | number | ((previous: string | number | null) => number),
  ) => {
    const value =
      typeof lineCount === 'function'
        ? lineCount(lineCountStateRef.current)
        : lineCount;
    lineCountStateRef.current = value;
    _setLineCount(value);
  };
  const [isMouseDown, _setIsMouseDown] = useState<boolean | null>(null);
  const isMouseDownStateRef = useRef(isMouseDown);
  const setIsMouseDown = (isMouseDown: boolean) => {
    isMouseDownStateRef.current = isMouseDown;
    _setIsMouseDown(isMouseDown);
  };
  const [isDrawingNewLine, _setIsDrawingNewLine] = useState<boolean | null>(
    null,
  );
  const isDrawingNewLineStateRef = useRef(isDrawingNewLine);
  const setIsDrawingNewLine = (isDrawingNewLine: boolean) => {
    isDrawingNewLineStateRef.current = isDrawingNewLine;
    _setIsDrawingNewLine(isDrawingNewLine);
  };
  const [pathLine, _setPathLine] = useState<FabricObject | null>(null);
  const pathLineStateRef = useRef(pathLine);
  const setPathLine = (pathLine: FabricObject | null) => {
    pathLineStateRef.current = pathLine;
    _setPathLine(pathLine);
  };
  const [startX, _setStartX] = useState<number | null>(null);
  const startXStateRef = useRef(startX);
  const setStartX = (startX: number) => {
    startXStateRef.current = startX;
    _setStartX(startX);
  };
  const [startY, _setStartY] = useState<number | null>(null);
  const startYStateRef = useRef(startY);
  const setStartY = (startY: number) => {
    startYStateRef.current = startY;
    _setStartY(startY);
  };

  const [zoomLevel, setZoomLevel] = useState(1);

  const saveObjectState = (group: FabricCanvas) => {
    const allObjects = group.getObjects();
    // eslint-disable-next-line unicorn/consistent-function-scoping -- kept local to saveObjectState (its only caller) for cohesion; closes over ToolContextState.scaleStateRef
    const saveStates = (objects: FabricObject[]) => {
      for (const object of objects) {
        if (object.name!.includes('line')) continue;
        object.originScaleX =
          (object.scaleX / ToolContextState.scaleStateRef.current) * 1;
        object.originScaleY =
          (object.scaleY / ToolContextState.scaleStateRef.current) * 1;
        object.originLeft = parseInt(
          String((object.left / ToolContextState.scaleStateRef.current) * 1),
        );
        object.originTop = parseInt(
          String((object.top / ToolContextState.scaleStateRef.current) * 1),
        );
      }
    };
    if (allObjects?.[0]?.group) {
      saveStates(allObjects[0].group.getObjects());
    } else saveStates(allObjects);
  };

  const isWithinCanvas = (canvas: FabricCanvas, movingItem: FabricObject) => {
    let isWithin = true;
    const correctionX =
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- originX is a deprecated getter; read here for the non-center positioning math
      movingItem.originX === 'center' ? movingItem.width / 2 : 0;
    const correctionY =
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- originY is a deprecated getter; read here for the non-center positioning math
      movingItem.originY === 'center' ? movingItem.height / 2 : 0;

    movingItem.right = movingItem.left + movingItem.width - correctionX;
    movingItem.bottom = movingItem.top + movingItem.height - correctionY;

    const borderBuffer = 5;

    if (movingItem.right > canvas.width - borderBuffer) {
      movingItem.left =
        canvas.width - movingItem.width - borderBuffer + correctionX;
      isWithin = false;
    }
    if (movingItem.bottom > canvas.height - borderBuffer) {
      movingItem.top =
        canvas.height - movingItem.height - borderBuffer + correctionY;
      isWithin = false;
    }
    if (movingItem.left - correctionX < borderBuffer) {
      movingItem.left = borderBuffer + correctionX;
      isWithin = false;
    }
    if (movingItem.top - correctionY < borderBuffer) {
      movingItem.top = borderBuffer + correctionY;
      isWithin = false;
    }

    return isWithin;
  };

  const onSelectionCreated = (event: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    const activeObjects = canvas.getActiveObjects();

    // Descend into each group's private _objects to strip line parts — the only way Fabric exposes to remove part of a selection.
    for (const object of activeObjects) {
      const name = object.name;
      if (
        [
          'arrowHead',
          'pointStart',
          'pointCurve',
          'pointEnd',
          undefined,
        ].includes(name) &&
        object.group
      ) {
        for (const innerObject of object.group.getObjects()) {
          const name = innerObject.name;
          if (
            [
              'arrowHead',
              'pointStart',
              'pointCurve',
              'pointEnd',
              undefined,
            ].includes(name)
          ) {
            object.group?.remove(innerObject);
          }
        }
      }
    }

    const newSelection = event.selected ?? null;
    // The selection:created payload is { e, selected } with NO `target`; the active object (or the
    // ActiveSelection for a multi-select) comes from `getActiveObject()`. Without this fallback the
    // handler throws "Cannot read properties of undefined (reading 'set')" on every selection, aborting
    // the control-styling below (default Fabric controls leak through).
    const target = event.target ?? canvas.getActiveObject();
    const targetVariation = target?.variation;

    ToolContextState.setSelection(newSelection);
    canvas.requestRenderAll();

    if (targetVariation === 'marker') {
      target!.set({
        ...selectionStyles,
        lockScalingX: false,
        lockScalingY: false,
        hideBorders: false,
        cornerColor: 'rgba(243, 108, 33, 30)',
        cornerSize: 12,
        cornerStyle: 'circle',
        transparentCorners: true,
        originLeft: (target!.left / ToolContextState.scaleStateRef.current) * 1,
        originTop: (target!.top / ToolContextState.scaleStateRef.current) * 1,
        hasBorders: false,
      });

      target!.setControlsVisibility({
        mt: false,
        mb: false,
        ml: false,
        mr: false,
        tm: false,
        bm: false,
        mtr: false,
      });
    } else {
      target!.set({
        ...selectionStyles,
        lockScalingX: true,
        lockScalingY: true,
        hideBorders: false,
        originLeft: (target!.left / ToolContextState.scaleStateRef.current) * 1,
        originTop: (target!.top / ToolContextState.scaleStateRef.current) * 1,
      });

      target!.setControlsVisibility({
        mt: false,
        mb: false,
        ml: false,
        mr: false,
        tl: false,
        tm: false,
        tr: false,
        bl: false,
        br: false,
        bm: false,
        mtr: false,
      });
    }
    saveObjectState(canvas);
  };

  const onSelectionCleared = (event: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    const lastSelection = event.deselected;
    const activeObjs = (event && lastSelection) ?? canvas.getActiveObjects();

    for (const activeObj of activeObjs) {
      if (!activeObj) {
        continue;
      }

      ToolContextState.setSelection(null);
      toggleLineControlVisibility({ show: true, canvas });
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    }
    saveObjectState(canvas);
  };

  const onSelectionUpdated = (event: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    // selection:updated is { e, selected, deselected } — no `target` (see onSelectionCreated).
    const target = event.target ?? canvas.getActiveObject();
    const targetVariation = target?.variation;

    if (targetVariation === 'marker') {
      target!.set({
        // ...selectionStyles,
        lockScalingX: false,
        lockScalingY: false,
        lockUniScaling: false,
        hasBorders: false,
      });

      target!.setControlsVisibility({
        mt: false,
        mb: false,
        ml: false,
        mr: false,
        tm: false,
        bm: false,
        mtr: false,
      });
    } else {
      target!.set({
        ...selectionStyles,
        lockScalingX: true,
        lockScalingY: true,
      });

      target!.setControlsVisibility({
        mt: false,
        mb: false,
        ml: false,
        mr: false,
        tl: false,
        tm: false,
        tr: false,
        bl: false,
        br: false,
        bm: false,
        mtr: false,
      });
    }

    ToolContextState.setSelection(canvas.getActiveObjects());
    canvas.requestRenderAll();
    saveObjectState(canvas);
  };

  const onMouseOver = (event: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;

    // Disable items being selected when arrow is being drawn
    if (ToolContextState.storeLineTypeStateRef.current) setSelectable(false);

    const target = event.target;
    if (target) {
      // We have run this here so lines will be rendered properly
      const active = canvas
        .getActiveObjects()
        .filter((obj) =>
          [
            target,
            target.pointStart,
            target.pointEnd,
            target.pointCurve,
          ].includes(obj),
        );
      if (active.length === 0) {
        toggleLineControlVisibility({ show: true, canvas: canvas });
      }
    }
  };

  const onMouseOut = (event: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    const target = event.target;

    if (!target) {
      // when mouse leaves the canvas, remove active styles from all line point controls
      toggleLineControlVisibility({ show: false, canvas: canvas });
    }
  };

  const onMouseDown = (event: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    canvas.selection = !ToolContextState.isMobileStateRef.current;
    const mouseEvent = event.e as MouseEvent;
    const target = event?.target;
    const targetVariation = target?.variation;
    const zoomLevel = canvas?.getZoom();

    if (mouseEvent.altKey && zoomLevel >= 1 && zoomLevel < 2) {
      // Makes it possible to drag over the field with the alt key
      (document.querySelector('.upper-canvas') as HTMLElement).style.cursor =
        'move';
      canvas.isDragging = true;
      canvas.selection = false;
      canvas.lastPosX = mouseEvent.clientX;
      canvas.lastPosY = mouseEvent.clientY;
    } else {
      if (canvas) canvas.isSelecting = true;
    }

    // Bring all other objects in front of hightlight markers that are dragged.
    if (targetVariation === 'marker') {
      for (const object of canvas.getObjects()) {
        if (object.variation !== 'marker') canvas.bringObjectToFront(object);
      }
    }
    if (targetVariation === 'field-text' || targetVariation === 'marker')
      return;

    // don't draw a line unless a line type is selected
    if (ToolContextState.storeLineTypeStateRef.current) {
      setIsMouseDown(true);
      if (!target) {
        toggleLineControlVisibility({ show: true, canvas: canvas });
        const { x, y } = canvas.getScenePoint(mouseEvent);
        setIsDrawingNewLine(true);
        setStartX(x);
        setStartY(y);
        const newPathLine = makePathLine(
          ToolContextState.storeLineTypeStateRef.current,
          ToolContextState.scaleStateRef.current,
        );
        setPathLine(newPathLine);
        if (canvas) canvas?.add(newPathLine);
      }
    }

    saveObjectState(canvas);
  };

  const onMouseMove = (event: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    const startX = startXStateRef.current;
    const startY = startYStateRef.current;
    const mouseEvent = event.e as MouseEvent;

    if (canvas?.isDragging) {
      canvas.viewportTransform[4] += mouseEvent.clientX - canvas.lastPosX!;
      canvas.viewportTransform[5] += mouseEvent.clientY - canvas.lastPosY!;
      canvas.requestRenderAll();
      canvas.lastPosX = mouseEvent.clientX;
      canvas.lastPosY = mouseEvent.clientY;
    }

    if (isMouseDownStateRef.current && isDrawingNewLineStateRef.current) {
      const { x, y } = canvas.getScenePoint(mouseEvent);

      // midpoint of start/end — where the curve controller is dropped
      const midpointX = (startX! + x) * 0.5;
      const midpointY = (startY! + y) * 0.5;

      const yStartingPos = startY!;

      // Everything crashes on Firefox if yStartingPos isn't rounded up ¯\_(ツ)_/¯
      pathLineStateRef.current?.set({
        path: [
          ['M', startX, yStartingPos.toFixed(2)],
          ['Q', midpointX, midpointY, x, y],
        ],
        // `aCoords` corners must be `Point` INSTANCES. During the draw drag Fabric runs live hit-testing
        // (`_searchPossibleTargets` → `isPointInPolygon` → `isPointContained`) which calls `.eq()` on
        // these corners; a plain {x,y} object lacks it → "e.eq is not a function" on every mousemove.
        // Points keep the exact degenerate-bbox-at-endpoint behaviour.
        aCoords: {
          tl: new Point(x, y),
          tr: new Point(x, y),
          bl: new Point(x, y),
          br: new Point(x, y),
        },
      } as unknown as Partial<FabricObject>);
      canvas.requestRenderAll();
    }
  };

  const onMouseUp = (e: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    const startX = startXStateRef.current;
    const startY = startYStateRef.current;
    canvas.isDragging = false;
    canvas.isSelecting = false;

    const objects = canvas.getObjects();

    for (const object of objects) {
      if (object.variation !== undefined) {
        object.setCoords();
      }
    }
    if (e.target?.variation === 'field-text') return;
    setIsMouseDown(false);

    const { x, y } = canvas.getScenePoint(e.e!);

    // TODO: maybe add some kind of threshold?
    // the mouse didn't drag, it just clicked, so dont do anything further
    if (x === startX && y === startY) {
      setIsDrawingNewLine(false);
      return;
    }

    // make sure that
    // a) were not just clicking an object
    // b) we're not mousing up on an object after a line drag
    if (isDrawingNewLineStateRef.current) {
      setIsDrawingNewLine(false);

      const midpointX = (startX! + x) * 0.5;
      const midpointY = (startY! + y) * 0.5;

      const angle = calcArrowAngle(midpointX, midpointY, x, y);
      const pointStart = makeCurveEnd(startX!, startY!);
      const pointCurve = makeCurvePoint(
        midpointX,
        midpointY,
        ToolContextState.isMobileStateRef.current,
      );
      const pointEnd = makeCurveEnd(x, y);
      const arrowHead = makeArrowHead(
        x,
        y,
        angle,
        ToolContextState.scaleStateRef.current,
      );

      pointStart.id = `id-${lineCountStateRef.current}`;
      pointCurve.id = `id-${lineCountStateRef.current}`;
      pointEnd.id = `id-${lineCountStateRef.current}`;
      setLineCount((previousCount) => Number(previousCount) + 1);

      pointStart.name = 'pointStart';
      pointCurve.name = 'pointCurve';
      pointEnd.name = 'pointEnd';

      pointStart.variation = ToolContextState.storeLineTypeStateRef.current;
      pointCurve.variation = ToolContextState.storeLineTypeStateRef.current;
      pointEnd.variation = ToolContextState.storeLineTypeStateRef.current;

      // Cross-link every point to each other and the line, so deleting any one point deletes the whole line.
      pathLineStateRef.current!.pointStart = pointStart;
      pathLineStateRef.current!.arrowHead = arrowHead;
      pathLineStateRef.current!.pointCurve = pointCurve;
      pathLineStateRef.current!.pointEnd = pointEnd;

      pointStart.pathLine = pathLineStateRef.current!;
      pointStart.arrowHead = arrowHead;
      pointStart.pointCurve = pointCurve;
      pointStart.pointEnd = pointEnd;

      pointCurve.pathLine = pathLineStateRef.current!;
      pointCurve.arrowHead = arrowHead;
      pointCurve.pointStart = pointStart;
      pointCurve.pointEnd = pointEnd;

      pointEnd.pathLine = pathLineStateRef.current!;
      pointEnd.arrowHead = arrowHead;
      pointEnd.pointStart = pointStart;
      pointEnd.pointCurve = pointCurve;

      canvas.add(arrowHead);
      canvas.add(pointStart);
      canvas.add(pointCurve);
      canvas.add(pointEnd);

      setStartX(0);
      setStartY(0);

      // make the just-drawn line active — any of its points works
      canvas.setActiveObject(pointCurve);

      toggleLineControlVisibility({
        show: true,
        objects: [pointStart, pointCurve, pointEnd],
        canvas: canvas,
      });
      ToolContextState.storeAddItem(pointCurve);
      ToolContextState.setStoreLineType('');
      canvas.defaultCursor = ''; // reset the custom cursor
      ToolContextState.setLineSelected(null);
      setPathLine(null);

      // Enable selecting of items after placing arrow
      setSelectable(true);
    } else {
      // make sure there's an object being dragged
      if (!e.target) return;
      // if user drags an object off the canvas, remove it
      if (canvas.getActiveObjects().length > 1) {
        // if (x > canvas.width || x < 0 || y > canvas.height || y < 0) removeActiveItem(canvas)
      }
    }

    canvas.requestRenderAll();
    saveObjectState(canvas);
  };

  const onObjectMoving = (event: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    const activeObjects = canvas.getActiveObjects();
    const movingItem = event.target!;
    const movingName = movingItem?.name;
    const movingGroup = movingItem?.group;
    const mouseEvent = event.e as MouseEvent;

    // Logic for restraining moving objects to the canvas border
    const isWithin = isWithinCanvas(canvas, movingItem);
    if (!isWithin) return;

    const pathLineObjMov = movingItem.pathLine;
    for (const obj of activeObjects) {
      // `aCoords` is the "has geometry" marker. `active` is a custom flag written here but never read —
      // an intentional no-op (leaving the write avoids touching the draw path's behaviour).
      if (obj.aCoords) obj.active = false;
    }

    if (pathLineObjMov) {
      // from here on, we're only concerned with modifying an existing line
      const { x, y } = canvas.getScenePoint(mouseEvent);

      const path = (pathLineObjMov as unknown as { path: number[][] }).path;
      const path01 = path[0][1];
      const path02 = path[0][2];
      const path11 = path[1][1];
      const path12 = path[1][2];
      const path13 = path[1][3];
      const path14 = path[1][4];

      if (event.multiSelect === true) {
        // eslint-disable-next-line unicorn/prefer-switch -- 3 branches on movingName with distinct multi-line path math; the else-if chain reads clearer than a switch here
        if (movingName === 'pointStart') {
          pathLineObjMov.set({
            path: [
              [
                'M',
                movingGroup!.left + movingItem.left,
                movingGroup!.top + movingItem.top,
              ],
              ['Q', path11, path12, path13, path14],
            ],
          } as unknown as Partial<FabricObject>);
        } else if (movingName === 'pointCurve') {
          pathLineObjMov.set({
            path: [
              ['M', path01, path02],
              [
                'Q',
                movingGroup!.left + movingItem.left,
                movingGroup!.top + movingItem.top,
                path13,
                path14,
              ],
            ],
          } as unknown as Partial<FabricObject>);
          movingItem.arrowHead!.set({
            angle: calcArrowAngle(path11, path12, path13, path14),
          });
        } else if (movingName === 'pointEnd') {
          pathLineObjMov.set({
            path: [
              ['M', path01, path02],
              [
                'Q',
                path11,
                path12,
                movingGroup!.left + movingItem.left,
                movingGroup!.top + movingItem.top,
              ],
            ],
          } as unknown as Partial<FabricObject>);

          movingItem.arrowHead!.set({
            left: movingGroup!.left + movingItem.left,
            top: movingGroup!.top + movingItem.top,
            angle: calcArrowAngle(path11, path12, path13, path14),
          });
        }
      } else {
        // eslint-disable-next-line unicorn/prefer-switch -- 3 branches on movingName with distinct multi-line path math; the else-if chain reads clearer than a switch here
        if (movingName === 'pointStart') {
          pathLineObjMov.set({
            path: [
              ['M', x, y],
              ['Q', path11, path12, path13, path14],
            ],
          } as unknown as Partial<FabricObject>);
        } else if (movingName === 'pointCurve') {
          pathLineObjMov.set({
            path: [
              ['M', path01, path02],
              ['Q', x, y, path13, path14],
            ],
          } as unknown as Partial<FabricObject>);
          movingItem.arrowHead!.set({
            angle: calcArrowAngle(path11, path12, path13, path14),
          });
        } else if (movingName === 'pointEnd') {
          pathLineObjMov.set({
            path: [
              ['M', path01, path02],
              ['Q', path11, path12, x, y],
            ],
          } as unknown as Partial<FabricObject>);
          movingItem.arrowHead!.set({
            left: x,
            top: y,
            angle: calcArrowAngle(path11, path12, path13, path14),
          });
        }
      }

      canvas.renderAll();
    }
    saveObjectState(canvas);
  };

  const onObjectScaled = () => {
    saveObjectState(ToolContextState.canvasStateRef.current);
  };

  const onObjectAdded = (event: FabricEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    if (canvas?.hasLoaded) saveObjectState(canvas);

    const target = event.target;
    const targetVariation = target?.variation;

    if (!targetVariation) return;
    const isGoal = targetVariation?.includes('goal');
    if (isGoal) {
      // goals always start at the back (until they're rotated)
      canvas.sendObjectToBack(target!);
    } else {
      // this fixes a bug where if the first item dropped is a goal, then the second item dropped
      // will automatically go behind it
      if (canvas.getObjects().length > 0) return;
      // any other item gets nudged backwards once, so it sits behind the top-facing goal
      canvas.sendObjectBackwards(target!);
    }
  };
  const setSelectable = (isSelectable: boolean) => {
    for (const item of ToolContextState.canvasStateRef.current.getObjects()) {
      item.selectable = isSelectable;
      item.evented = isSelectable;
    }
  };

  let firstVal: number;
  let zoomScale: number;

  const pinchStart = (e: TouchEvent) => {
    if (e.touches.length !== 2) {
      return;
    }

    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    const finger1x = e.touches[0].pageX;
    const finger1y = e.touches[0].pageY;
    const finger2x = e.touches[1].pageX;
    const finger2y = e.touches[1].pageY;
    const centerX = (finger1x + finger2x) / 2;
    const centerY = (finger1y + finger2y) / 2;
    const zoomStartValue = canvas.getZoom();
    const viewportCenter = canvas.getVpCenter();
    const panStartTouchCenter = { x: centerX, y: centerY };

    canvas.zoomStartValue = zoomStartValue;
    canvas.viewportCenter = viewportCenter;
    canvas.panStartTouchCenter = panStartTouchCenter;

    firstVal = Math.hypot(
      e.touches[0].pageX - e.touches[1].pageX,
      e.touches[0].pageY - e.touches[1].pageY,
    );

    canvas.selection = false;
  };
  const pinchMove = (e: TouchEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;

    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY,
      );

      zoomScale =
        canvas.zoomStartValue! +
        (Math.round(dist) - Math.round(firstVal)) / 100;

      if (zoomScale > 2) zoomScale = 2;
      else if (zoomScale < 0.9) zoomScale = 0.9;

      if (canvas.panStartTouchCenter && canvas.viewportCenter) {
        const finger1x = e.touches[0].pageX;
        const finger1y = e.touches[0].pageY;

        const finger2x = e.touches[1].pageX;
        const finger2y = e.touches[1].pageY;

        const centerX = (finger1x + finger2x) / 2;
        const centerY = (finger1y + finger2y) / 2;

        const viewportCenterStart = canvas.viewportCenter;
        const panStartTouchCenter = canvas.panStartTouchCenter;

        const panPoint = new Point(
          viewportCenterStart.x -
            -(panStartTouchCenter.x - centerX) -
            (canvas.getWidth() / 2) * (1 / zoomScale),
          viewportCenterStart.y -
            -(panStartTouchCenter.y - centerY) -
            (canvas.getHeight() / 2) * (1 / zoomScale),
        );
        canvas.setZoom(zoomScale);
        canvas.absolutePan(panPoint);
        // Set this state for the reset zoom button
        setZoomLevel(zoomScale);
      }
    }
  };
  const pinchEnd = (_e: TouchEvent) => {
    const canvas: FabricCanvas = ToolContextState.canvasStateRef.current;
    canvas.viewportCenter = null;
    canvas.panStartTouchCenter = null;
  };

  useEffect(() => {
    if (zoomLevel !== 1) {
      ToolContextState.setEnableResetButton(true);
    }
  }, [zoomLevel]);

  useEffect(() => {
    const canvasContainer = document.querySelector('.canvas-container');

    if (ToolContextState.isMobileStateRef.current) {
      canvasContainer?.addEventListener(
        'touchstart',
        pinchStart as EventListener,
      );
      canvasContainer?.addEventListener(
        'touchmove',
        pinchMove as EventListener,
      );
      canvasContainer?.addEventListener('touchend', pinchEnd as EventListener);
      return () => {
        canvasContainer?.removeEventListener(
          'touchstart',
          pinchStart as EventListener,
        );
        canvasContainer?.removeEventListener(
          'touchmove',
          pinchMove as EventListener,
        );
        canvasContainer?.removeEventListener(
          'touchend',
          pinchEnd as EventListener,
        );
      };
    }
  }, [ToolContextState.isMobileStateRef.current]);

  useEffect(() => {
    if (!canvasRef.current) return;
    // Idempotent init: StrictMode double-invokes this effect in dev; guard against creating a second
    // Fabric canvas on the same element.
    if (ToolContextState.canvasStateRef.current instanceof StaticCanvas) return;
    ToolContextState.setCanvasRef(canvasRef);
    ToolContextState.setCanvas(initialiseCanvas(canvasRef.current));
  }, [canvasRef]);

  // Change background colors when switching to indoor
  useEffect(() => {
    let backgroundColor: string;
    let backgroundColorOutside: string;

    if (ToolContextState.isDugoutTheme) {
      // Dugout keys the surround on `pitchType`, not indoor/outdoor: green pitch vs blue futsal
      // (Dugout's own values). Every dugout pitch PNG is fully opaque with its own surround baked in —
      // #69C292 for the default pitches, blue for futsal — so this fill is not showing through the
      // artwork; it matches it, which keeps the edges and the pre-load state consistent. Keep the two
      // in sync when regenerating a pitch PNG. No indoor swap here (Dugout has no rooms), so
      // `isIndoorActive` is left untouched.
      const isFutsal = ToolContextState.fieldSelected?.pitchType === 'futsal';
      backgroundColor = isFutsal ? '#00AADB' : '#69C292';
      backgroundColorOutside = isFutsal ? '#63B8CF' : '#5AAF81';
    } else {
      let isField;

      if (ToolContextState.fieldSelected?.variation.includes('field')) {
        isField = true;
        ToolContextState.setIsIndoorActive(false);
      } else {
        isField = false;
        ToolContextState.setIsIndoorActive(true);
      }

      backgroundColor =
        ToolContextState.isIndoorActive || !isField ? '#4DC5E6' : '#6BC294';
      backgroundColorOutside =
        ToolContextState.isIndoorActive || !isField ? '#33BDE2' : '#5AAF81';
    }

    (
      document.querySelector('.canvas-editor-container') as HTMLElement
    ).style.backgroundColor = backgroundColorOutside;
    ToolContextState.canvasStateRef.current.backgroundColor = backgroundColor;
    ToolContextState.canvasStateRef.current.renderAll();
  }, [ToolContextState.isIndoorActive, ToolContextState.fieldSelected]);

  useEffect(() => {
    ToolContextState.canvasStateRef?.current?.renderAll();
    ToolContextState.canvasStateRef?.current?.on({
      'selection:cleared': onSelectionCleared,
      'selection:updated': onSelectionUpdated,
      'selection:created': onSelectionCreated,
      'mouse:up': onMouseUp,
      'mouse:move': onMouseMove,
      'mouse:over': onMouseOver,
      'mouse:down': onMouseDown,
      'mouse:out': onMouseOut,
      'object:moving': onObjectMoving,
      'object:scaled': onObjectScaled,
      'object:added': onObjectAdded,
    });
    return () => {
      ToolContextState.canvasStateRef?.current?.off({
        'selection:cleared': onSelectionCleared,
        'selection:updated': onSelectionUpdated,
        'selection:created': onSelectionCreated,
        'mouse:up': onMouseUp,
        'mouse:move': onMouseMove,
        'mouse:over': onMouseOver,
        'mouse:down': onMouseDown,
        'mouse:out': onMouseOut,
        'object:moving': onObjectMoving,
        'object:scaled': onObjectScaled,
        'object:added': onObjectAdded,
      });
    };
  }, [ToolContextState.canvasStateRef.current]);

  return (
    <canvas id="canvas" ref={canvasRef} aria-label={t('DRWNG__A11Y_CANVAS')} />
  );
};

export default Canvas;
